import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { usePrerequisites } from "../components/GettingStarted";
import { PlanPicker, defaultPlan, describePlan, formatPrice } from "../components/PlanPicker";
import { RegionPicker } from "../components/RegionPicker";
import { summarize } from "../components/RegionSection";
import { RepoPicker, type RepoSelection } from "../components/RepoPicker";
import { Icon } from "../components/icons";
import { Alert, Button, Card, Field, Input, PageHeader, Select, cx } from "../components/ui";
import { QuotaAlert, quotaRefusal, type QuotaRefusal } from "../components/Quota";
import { ApiError } from "../lib/api";
import { BUILD_TYPES } from "../lib/buildTypes";
import { EnvEditor } from "../components/EnvEditor";
import { emptyRow, envPayload, envProblems, summarizeEnv, type EnvRow } from "../lib/envRows";
import { useCreateApp, usePlans, useRegions } from "../lib/queries";
import type { BuildType, CreateAppPayload } from "../lib/types";

/*
 * A repository is the only source the form offers. The API still accepts a
 * container image, and apps created that way keep working; the wizard stopped
 * offering it because the product is push-to-deploy, and a second card that
 * skips the build sent people down a path the rest of the dashboard — build
 * logs, deploy history, the webhook — is not built around.
 */
/* The three steps, by key. Their words are in `create.json` under `app.steps`. */
const STEPS = ["repository", "settings", "review"] as const;

export function NewAppPage() {
  const { t } = useTranslation("create");
  /* The build-type words live with the app's own settings screen, so both
     forms offer the same three in the same words. */
  const { t: appBuild } = useTranslation("app");
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const createApp = useCreateApp(currentOrg?.id ?? "");

  const plans = usePlans(currentOrg?.id);
  const regions = useRegions(currentOrg?.id);
  const prerequisites = usePrerequisites(currentOrg?.id);

  const [step, setStep] = useState(0);
  const [planId, setPlanId] = useState<string | null>(null);
  // Null is Auto, and Auto is the default — see the Region card.
  const [region, setRegion] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [repo, setRepo] = useState<RepoSelection | null>(null);
  const [autoDeploy, setAutoDeploy] = useState(true);
  const [buildPath, setBuildPath] = useState("/");
  const [buildType, setBuildType] = useState<BuildType>("railpack");
  const [port, setPort] = useState("3000");
  const [envRows, setEnvRows] = useState<EnvRow[]>(() => [emptyRow()]);
  const [deployOnCreate, setDeployOnCreate] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A refused create that was the ceiling, not a mistake: rendered with the numbers and the way to ask.
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);

  /*
   * Preselect a plan rather than leaving the field empty.
   *
   * An unset plan was the one thing that made this form impossible to submit
   * without a decision a first-time customer has no basis for. Which tier that
   * is lives in `defaultPlan`, with the reasoning: the smallest that runs an
   * ordinary application, not the smallest sold. The step says it can be
   * changed later, and it can.
   */
  useEffect(() => {
    if (planId !== null) return;
    const chosen = defaultPlan(plans.data ?? []);
    if (chosen) setPlanId(chosen.id);
  }, [plans.data, planId]);

  // Naming an app after its repository is what almost everyone wants, so do it
  // for them — but never overwrite something they typed themselves.
  const [nameTouched, setNameTouched] = useState(false);
  function selectRepo(selection: RepoSelection | null) {
    setRepo(selection);
    if (selection && !nameTouched) setName(selection.repository);
  }

  const env = summarizeEnv(envRows);
  const envSummary =
    env.count === 0
      ? t("app.envNone")
      : t("app.envCount", { count: env.count }) +
        (env.secrets > 0 ? t("app.envSecrets", { count: env.secrets }) : "");
  // What the folded {t("app.advanced")} line says: the choices made inside it, or, with
  // none made, what it holds.
  const advancedSet = [
    region !== null ? summarize(regions.data, region, t) : null,
    env.count > 0 ? envSummary : null,
  ].filter((part): part is string => part !== null);
  const advancedSummary =
    advancedSet.length > 0 ? advancedSet.join(" · ") : t("app.summary");
  const selectedPlan = (plans.data ?? []).find((plan) => plan.id === planId) ?? null;

  /*
   * What is missing, as a sentence, for the step the customer is on. Returning
   * the reason rather than a boolean means the Continue button can say why it is
   * disabled instead of just looking broken.
   */
  function blocker(forStep: number): string | null {
    if (forStep === 0) {
      if (!repo) return t("app.pickRepo");
      return null;
    }
    if (forStep === 1) {
      if (!name.trim()) return t("app.nameIt");
      if (!planId) return t("app.pickPlan");
      // The same words the row shows under itself, so the two cannot disagree.
      const envIssue = envProblems(envRows)[0];
      if (envIssue) return envIssue.message;
      return null;
    }
    return null;
  }

  const currentBlocker = blocker(step);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!currentOrg) return;
    /*
     * Only the Review step creates. Enter in a text field on an earlier step is
     * the browser's implicit submission and must not skip the review; the
     * step that does have the submit button is the only one whose submit
     * means "go".
     */
    if (step !== STEPS.length - 1) return;
    setError(null);
    setRefusal(null);

    // Both earlier steps are re-checked here, not just the current one: someone
    // can reach Review and then go back and empty a field.
    const problem = blocker(0) ?? blocker(1);
    if (problem) {
      setError(problem);
      return;
    }
    if (!planId) return;

    if (!repo) return;
    const source: CreateAppPayload["source"] = {
      type: "repo",
      connectionId: repo.connectionId,
      owner: repo.owner,
      repository: repo.repository,
      branch: repo.branch,
      buildPath,
      autoDeploy,
    };

    const parsedPort = Number.parseInt(port, 10);

    try {
      const created = await createApp.mutateAsync({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        source,
        planId,
        region,
        buildType,
        ...(Number.isFinite(parsedPort) ? { port: parsedPort } : {}),
        env: envPayload(envRows),
        deployOnCreate,
      });

      // The app exists either way. A warning means push-to-deploy did not get
      // armed, which is worth carrying to the next page rather than dropping.
      void navigate(`/apps/${created.id}`, {
        state: created.warning ? { warning: created.warning } : undefined,
      });
    } catch (cause) {
      setRefusal(quotaRefusal(cause));
      setError(cause instanceof ApiError ? cause.message : t("app.failed"));
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title={t("app.title")}
      />

      {/*
        A three-step wizard rather than one page of four cards. The old form put
        every decision on screen at once, including a plan that had no default,
        so a first-time customer met the whole thing before understanding any of
        it. One question at a time, and nothing can be submitted half-filled.
      */}
      <ol className="flex items-stretch border border-line">
        {STEPS.map((label, index) => {
          const state = index === step ? "current" : index < step ? "done" : "todo";
          return (
            <li key={label} className="flex-1">
              <button
                type="button"
                // Backwards only. Jumping ahead would skip the validation that
                // makes the next step answerable.
                disabled={index > step}
                onClick={() => setStep(index)}
                className={cx(
                  "flex w-full items-center gap-2 px-2.5 py-3 text-left text-xs transition-colors duration-200 ease-smooth sm:gap-2.5 sm:px-4 sm:text-sm",
                  index > 0 && "border-l border-line",
                  state === "current" && "bg-accent-soft/50 text-fg",
                  state === "done" && "text-fg-muted hover:bg-raised",
                  state === "todo" && "text-fg-subtle",
                )}
              >
                <span
                  className={cx(
                    "grid size-5 shrink-0 place-items-center text-[11px] tabular-nums sm:size-6 sm:text-xs",
                    state === "current" && "bg-accent text-white",
                    state === "done" && "bg-mint text-canvas",
                    state === "todo" && "border border-line text-fg-subtle",
                  )}
                >
                  {state === "done" ? <Icon name="check" className="size-3.5" /> : index + 1}
                </span>
                <span className="truncate">{label}</span>
              </button>
            </li>
          );
        })}
      </ol>

      <form onSubmit={handleSubmit} className="space-y-6">
        {refusal ? <QuotaAlert refusal={refusal} /> : error && <Alert>{error}</Alert>}

        {/*
          Shown from the first step, not on submit. The API refuses to create an
          app on an empty balance, and discovering that after picking a
          repository, a branch and a plan is the kind of thing people do not come
          back from.
        */}
        {prerequisites.needsCredit && (
          <Alert tone="warning">
            <span className="font-medium">{t("shared.noCredit")}</span> {t("app.creditNote")}{" "}
            <Link to="/settings/billing" className="font-medium underline">
              {t("shared.addCredit")}
            </Link>
          </Alert>
        )}

        {/* ------------------------------------------------ step 1: your code */}
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg text-fg">{t("app.steps.repository")}</h2>
            </div>

            <Card className="space-y-5 p-5">
              <RepoPicker orgId={currentOrg?.id} value={repo} onChange={selectRepo} />

              {repo && (
                <>
                  <label className="flex items-start gap-2.5 border border-line p-3 text-sm">
                    <input
                      type="checkbox"
                      checked={autoDeploy}
                      onChange={(e) => setAutoDeploy(e.target.checked)}
                      className="mt-0.5 size-4 accent-accent"
                    />
                    <span className="text-fg">{t("app.autoDeploy", { branch: repo.branch })}</span>
                  </label>

                  <Field
                    label={t("app.rootDirectory")}
                    htmlFor="buildPath"
                    hint={t("app.rootHint")}
                  >
                    <Input
                      id="buildPath"
                      value={buildPath}
                      onChange={(e) => setBuildPath(e.target.value)}
                    />
                  </Field>
                </>
              )}
            </Card>
          </div>
        )}

        {/* ------------------------------------------------- step 2: settings */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg text-fg">{t("app.steps.settings")}</h2>
            </div>

            <Card className="space-y-5 p-5">
              <Field label={t("app.name")} htmlFor="name">
                <Input
                  id="name"
                  value={name}
                  placeholder={repo ? repo.repository : "my-app"}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameTouched(true);
                  }}
                />
              </Field>

              <Field
                label={t("app.port")}
                htmlFor="port"
                hint={t("app.portHint")}
              >
                <Input
                  id="port"
                  type="number"
                  min={1}
                  max={65535}
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  className="sm:max-w-40"
                />
              </Field>
            </Card>

            <Card>
              <div className="border-b border-line px-5 py-4">
                <h3 className="text-fg">{t("app.size")}</h3>
                <p className="mt-1 text-sm text-fg-muted">{t("app.sizeHint")}</p>
              </div>
              <div className="p-5">
                <PlanPicker
                  plans={plans.data ?? []}
                  loading={plans.isLoading}
                  value={planId}
                  onChange={setPlanId}
                />
              </div>
            </Card>

            <Card className="p-5">
              <details className="group">
                <summary className="flex cursor-pointer items-center gap-1.5 text-sm text-fg marker:content-['']">
                  <Icon
                    name="chevron"
                    className="size-3.5 transition-transform duration-200 ease-smooth group-open:rotate-90"
                  />
                  Advanced
                  {/* Named so nobody has to open it to find out whether they
                      need to — an accordion that hides its contents twice over.
                      Once something inside is set, the line says what: a
                      chosen region and the variables pasted are the two
                      things a customer would otherwise open it to check. */}
                  <span className="text-fg-subtle" data-testid="advanced-summary">
                    — {advancedSummary}
                  </span>
                </summary>

                {/*
                  Region and environment live here, folded, so the default
                  form is Name, Port and Size — what every first deploy needs
                  and nothing it does not. Auto is the right region for most
                  people and most apps boot with no variables at all; the
                  editor is a click away for the ones that do.
                */}
                <div className="mt-5 space-y-6">
                  <section className="space-y-3">
                    <h4 className="text-[13px] font-medium text-fg">{t("app.region")}</h4>
                    <RegionPicker
                      catalogue={regions.data}
                      loading={regions.isLoading}
                      value={region}
                      onChange={setRegion}
                    />
                  </section>

                  <section className="space-y-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                      <h4 className="text-[13px] font-medium text-fg">{t("app.environment")}</h4>
                      <span className="text-[13px] text-fg-subtle" data-testid="env-summary">
                        {envSummary}
                      </span>
                    </div>
                    <EnvEditor rows={envRows} onChange={setEnvRows} />
                  </section>

                  <Field label={t("app.description")} htmlFor="description" hint={t("app.optional")}>
                    <Input
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </Field>

                  <Field
                    label={t("app.build")}
                      htmlFor="buildType"
                      hint={t("app.buildHint")}
                  >
                    <Select
                      id="buildType"
                      value={buildType}
                      onChange={(e) => setBuildType(e.target.value as BuildType)}
                    >
                      {BUILD_TYPES.map((value) => (
                        <option key={value} value={value}>
                          {appBuild(`build.${value}`)}
                        </option>
                      ))}
                    </Select>
                  </Field>

                </div>
              </details>
            </Card>
          </div>
        )}

        {/* --------------------------------------------------- step 3: review */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg text-fg">{t("app.steps.review")}</h2>
            </div>

            <Card className="divide-y divide-line">
              <Row label={t("app.name")} value={name} />
              {repo && (
                <>
                  <Row label={t("app.steps.repository")} value={`${repo.owner}/${repo.repository}`} mono />
                  <Row label={t("app.branch")} value={repo.branch} mono />
                  <Row
                    label={t("app.onPush")}
                    value={autoDeploy ? t("app.deploysAutomatically") : t("app.deployManually")}
                  />
                </>
              )}
              <Row label={t("app.rootDirectory")} value={buildPath} mono />
              <Row
                label={t("app.size")}
                value={
                  selectedPlan ? describePlan(selectedPlan) : "—"
                }
              />
              <Row
                label={t("app.cost")}
                value={
                  selectedPlan
                    ? `${formatPrice(selectedPlan.price.cents, selectedPlan.price.currency)}/mo max, billed hourly`
                    : "—"
                }
              />
              <Row label={t("app.region")} value={summarize(regions.data, region, t)} />
              <Row label={t("app.port")} value={port} mono />
              {env.count > 0 && (
                <Row
                  label={t("app.variables")}
                  value={`${env.count} set${env.secrets > 0 ? `, ${env.secrets} secret` : ""}`}
                />
              )}
            </Card>

            <label className="flex items-center gap-2.5 text-sm text-fg-muted">
              <input
                type="checkbox"
                checked={deployOnCreate}
                onChange={(e) => setDeployOnCreate(e.target.checked)}
                className="size-4 accent-accent"
              />
              {t("app.deployOnCreate")}
                            </label>
          </div>
        )}

        {/* ------------------------------------------------------- navigation */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={() => (step === 0 ? void navigate("/apps") : setStep(step - 1))}
          >
            {step === 0 ? t("shared.cancel") : t("shared.back")}
          </Button>

          <div className="flex items-center gap-3">
            {/* Says why, rather than presenting a dead button and leaving the
                customer to guess which field it meant. */}
            {currentBlocker && <p className="text-sm text-fg-subtle">{currentBlocker}</p>}

            {/*
              Keyed, and it matters. Without keys React sees the same component
              at the same position and updates the one <button> in place — so
              the click on Continue that moves to Review flipped that element's
              type to "submit" while the click was still being dispatched, and
              the browser then ran the button's activation as a form submit.
              Reaching Review created the app. Distinct keys make React replace
              the element, and the click ends on a button that is gone.
            */}
            {step < STEPS.length - 1 ? (
              <Button
                key="continue"
                type="button"
                variant="inverse"
                disabled={Boolean(currentBlocker)}
                onClick={() => setStep(step + 1)}
              >
                {t("shared.continue")}
                <Icon name="chevron" className="size-3.5" />
              </Button>
            ) : (
              <Button
                key="create"
                type="submit"
                variant="inverse"
                loading={createApp.isPending}
                disabled={prerequisites.needsCredit}
              >
                {deployOnCreate ? t("app.createAndDeploy") : t("app.create")}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

/*
 * Spans rather than dt/dd. These rows are siblings inside a Card, which renders a
 * div — a dt outside a dl is invalid, and wrapping each pair in its own dl to
 * satisfy the parser would be markup written for a linter rather than a reader.
 */
function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 px-5 py-3.5">
      <span className="text-sm text-fg-subtle">{label}</span>
      <span className={cx("min-w-0 break-words text-sm text-fg", mono && "font-mono text-xs break-all")}>
        {value || "—"}
      </span>
    </div>
  );
}
